import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d974dtf0d76aer3t5alg', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = (parsed as { code?: string })?.code;
      const serverMsg = ((parsed as { meta?: { message?: string } })?.meta?.message) || '';
      const isSpam = serverMsg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || t('newsletter.error'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('newsletter.error'));
    }
  };

  const midIndex = Math.ceil(categories.length / 2);
  const leftCats = categories.slice(0, midIndex);
  const rightCats = categories.slice(midIndex);

  return (
    <footer className="bg-foreground-900 text-background-50">
      <div className="w-full px-6 md:px-10 py-16 md:py-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Brand + Newsletter */}
            <div className="lg:col-span-5">
              <Link to="/" className="inline-block mb-6">
                <span className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  Kakobuy<span className="text-accent-400">Finds</span>
                </span>
              </Link>
              <p className="text-foreground-300 text-sm leading-relaxed mb-8 max-w-sm">
                {t('footer.tagline')}
              </p>

              {/* Newsletter */}
              <div className="max-w-sm">
                <p className="text-sm font-semibold text-background-50 mb-3">
                  {t('newsletter.title')}
                </p>
                <p className="text-xs text-foreground-400 mb-4">
                  {t('newsletter.subtitle')}
                </p>

                {status === 'success' ? (
                  <p className="text-sm text-accent-400 font-medium">{t('newsletter.success')}</p>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="flex gap-2">
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('newsletter.placeholder')}
                        required
                        className="flex-1 bg-foreground-800 border border-foreground-700 rounded-lg px-4 py-2.5 text-sm text-background-50 placeholder:text-foreground-500 outline-none focus:border-accent-500 transition-colors"
                      />
                      <button
                        type="submit"
                        className="px-5 py-2.5 rounded-lg bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {t('newsletter.button')}
                      </button>
                    </div>
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute opacity-0 pointer-events-none"
                      style={{ position: 'absolute', left: '-9999px' }}
                    />
                    {status === 'error' && (
                      <p className="text-xs text-red-400 mt-2">{errorMsg}</p>
                    )}
                  </form>
                )}
              </div>
            </div>

            {/* Category Columns */}
            <div className="lg:col-span-4">
              <p className="text-sm font-semibold text-background-50 mb-4">{t('nav.categories')}</p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                <div className="flex flex-col gap-1.5">
                  {leftCats.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.namePl}
                    </a>
                  ))}
                </div>
                <div className="flex flex-col gap-1.5">
                  {rightCats.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.namePl}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-3">
              <p className="text-sm font-semibold text-background-50 mb-4">{t('nav.articles')}</p>
              <div className="flex flex-col gap-2">
                <Link to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik" className="text-sm text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap">
                  Kakobuy Spreadsheet — Przewodnik
                </Link>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">
                  Kakobuy Sheet
                </a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">
                  Buy It Now
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-14 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground-500">{t('footer.copyright')}</p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors whitespace-nowrap">
                {t('footer.privacy')}
              </a>
              <a href="#" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors whitespace-nowrap">
                {t('footer.terms')}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a href="#" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-accent-400 hover:border-accent-500 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-sm"></i>
              </a>
              <a href="#" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-accent-400 hover:border-accent-500 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <a href="#" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-accent-400 hover:border-accent-500 transition-colors cursor-pointer">
                <i className="ri-reddit-line text-sm"></i>
              </a>
              <a href="#" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-accent-400 hover:border-accent-500 transition-colors cursor-pointer">
                <i className="ri-telegram-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}